import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Anomaly, Forecast, GlobalHistoryPoint, TimeRange } from '../api/types'
import { Empty, ErrorBanner, Loading } from '../components/Common'
import { TimeRangePicker } from '../components/TimeRangePicker'
import { usePolling } from '../hooks/usePolling'
import { usePreference } from '../hooks/usePreferences'
import { Card } from '../components/Card'
import { formatValue, timeAgo, timeTickFormatter } from '../theme/charts'
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'

/**
 * Predictive view: where things are heading, and what is behaving oddly.
 */
export function Trends() {
  const [range, setRange] = usePreference<TimeRange>('range.trends', 'month')

  const forecasts = usePolling<Forecast[]>(() => api.forecasts(), 300_000)
  const anomalies = usePolling<Anomaly[]>(() => api.anomalies(range), 300_000, [range])
  const scores = usePolling<GlobalHistoryPoint[]>(() => api.statusHistory(range), 300_000, [range])

  return (
    <div className="stack">
      <div>
        <h1 className="page-title">Trends &amp; forecasts</h1>
        <p className="page-subtitle">
          Projections are a straight-line fit over daily averages, extrapolated
          to the threshold. They are meant to be argued with — the slope and the
          fit quality are shown so you can judge whether to believe them.
        </p>
      </div>

      <div className="toolbar">
        <TimeRangePicker value={range} onChange={setRange}
                         options={['week', 'month', 'quarter', 'year']} />
      </div>

      <Card
        title="Ecosystem health score"
        prefKey="trends.score"
        meta="hourly average, 100 = everything healthy"
        note="A weighted roll-up of every target: 100 when all are OK, 0 when every critical target is down. Recorded over time so slow degradation shows as a trend rather than only as a state change."
      >
        <>
          {scores.loading && !scores.data ? <Loading what="history" />
            : (scores.data ?? []).length === 0
              ? <Empty>No health history recorded yet.</Empty>
              : (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={scores.data ?? []} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="var(--grid-line)" vertical={false} />
                    <XAxis
                      dataKey="t"
                      stroke="var(--axis-line)"
                      tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                      tickLine={false}
                      minTickGap={48}
                      tickFormatter={timeTickFormatter(
                        range, (scores.data ?? []).map((p) => p.t))}
                    />
                    <YAxis
                      domain={[0, 100]}
                      stroke="var(--axis-line)"
                      tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      cursor={{ stroke: 'var(--text-muted)', strokeWidth: 1 }}
                      contentStyle={{
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border-hairline)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 12,
                      }}
                      labelFormatter={(v) => new Date(v as string).toLocaleString()}
                      formatter={(v: number) => [v.toFixed(1), 'Health score']}
                    />
                    <Area
                      type="monotone"
                      dataKey="score"
                      stroke="var(--series-1)"
                      strokeWidth={2}
                      fill="var(--series-1)"
                      fillOpacity={0.18}
                      isAnimationActive={false}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
        </>
      </Card>

      <section>
        <h2 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
          Capacity projections
        </h2>
        <ErrorBanner error={forecasts.error} />
        {forecasts.loading && !forecasts.data ? <Loading what="forecasts" />
          : (forecasts.data ?? []).length === 0
            ? <Empty>
                No projections yet. Forecasting needs at least a week of daily
                history before it will say anything.
              </Empty>
            : <ForecastTable forecasts={forecasts.data ?? []} />}
      </section>

      <section>
        <h2 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
          Seasonal anomalies
        </h2>
        <p className="page-subtitle">
          Readings that departed from what this metric normally does at this hour
          on this day of the week. Recorded for review — these do not send email.
        </p>
        {anomalies.loading && !anomalies.data ? <Loading what="anomalies" />
          : (anomalies.data ?? []).length === 0
            ? <Empty>Nothing unusual in this range.</Empty>
            : <AnomalyTable anomalies={anomalies.data ?? []} />}
      </section>
    </div>
  )
}

function ForecastTable({ forecasts }: { forecasts: Forecast[] }) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Target</th>
              <th>Metric</th>
              <th className="num">Now</th>
              <th className="num">Threshold</th>
              <th className="num">Change / day</th>
              <th className="num">Reaches it</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {forecasts.map((f) => (
              <tr key={`${f.targetId}|${f.metricKey}|${f.label}`}>
                <td>
                  <Link to={`/targets/${encodeURIComponent(f.targetId)}`}>{f.targetName}</Link>
                </td>
                <td>
                  <div>{f.displayName ?? f.metricKey}</div>
                  {f.label && <div className="key">{f.label}</div>}
                </td>
                <td className="num">{formatValue(f.currentValue, f.unit)}</td>
                <td className="num">{formatValue(f.threshold, f.unit)}</td>
                <td className="num">
                  {f.slopePerDay > 0 ? '+' : ''}{formatValue(f.slopePerDay, f.unit)}
                </td>
                <td className="num">
                  {f.daysToBreach === undefined || f.daysToBreach === null ? (
                    <span className="muted">not on this trend</span>
                  ) : (
                    <span style={{
                      color: f.daysToBreach < 14
                        ? 'var(--status-critical)'
                        : f.daysToBreach < 45 ? 'var(--status-warning)' : 'inherit',
                      fontWeight: f.daysToBreach < 45 ? 600 : 400,
                    }}>
                      {f.daysToBreach.toFixed(0)} days
                    </span>
                  )}
                </td>
                <td>
                  {f.confidence}
                  {f.rSquared !== undefined && f.rSquared !== null && (
                    <span className="muted"> (R² {f.rSquared.toFixed(2)})</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AnomalyTable({ anomalies }: { anomalies: Anomaly[] }) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Target</th>
              <th>Metric</th>
              <th className="num">Observed</th>
              <th className="num">Expected</th>
              <th className="num">Deviation</th>
            </tr>
          </thead>
          <tbody>
            {anomalies.map((a) => (
              <tr key={`${a.time}|${a.targetId}|${a.metricKey}|${a.label}`}>
                <td>
                  <div>{new Date(a.time).toLocaleString()}</div>
                  <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>{timeAgo(a.time)}</div>
                </td>
                <td>
                  <Link to={`/targets/${encodeURIComponent(a.targetId)}`}>{a.targetName}</Link>
                </td>
                <td>
                  <span className="key">{a.metricKey}</span>
                  {a.label && <span className="muted"> [{a.label}]</span>}
                </td>
                <td className="num">{a.observed.toFixed(2)}</td>
                <td className="num muted">{a.expected.toFixed(2)}</td>
                <td className="num">
                  <span style={{
                    color: a.direction === 'above'
                      ? 'var(--status-warning)' : 'var(--status-serious)',
                    fontWeight: 600,
                  }}>
                    {a.direction === 'above' ? '▲' : '▼'} {Math.abs(a.zScore).toFixed(1)}σ
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
